import {LinkExternalIcon} from '@primer/octicons-react'
import {Banner, Dialog, FormControl, Link, Spinner, ToggleSwitch} from '@primer/react'

import styles from './TriageAgentDialog.module.css'
import {useTriageAgentStatus, useUpdateTriageAgentStatus} from './use-triage-agent-status'

type TriageAgentDialogProps = {
  repoOwner: string
  repoName: string
  onClose: () => void
}

export function TriageAgentDialog({repoOwner, repoName, onClose}: TriageAgentDialogProps) {
  // TODO: retrieve the default branch name dynamically
  const configUrl = `/${repoOwner}/${repoName}/blob/main/.github/issue-triage.md`

  const {data, isLoading, isError: isFetchError} = useTriageAgentStatus(repoOwner, repoName)
  const {
    updateStatus,
    isPending: isUpdating,
    isError: isUpdateError,
    reset: resetMutation,
  } = useUpdateTriageAgentStatus(repoOwner, repoName)

  const handleToggle = () => {
    resetMutation()
    updateStatus(!data?.enabled)
  }

  const hasError = isFetchError || isUpdateError

  return (
    <Dialog title="Triage agent" onClose={onClose}>
      <div className={styles.dialogContent}>
        {isLoading ? (
          <div className={styles.loadingContainer}>
            <Spinner size="large" />
          </div>
        ) : (
          <>
            {hasError && (
              <Banner variant="critical" className={styles.errorBanner}>
                <Banner.Title>Something went wrong</Banner.Title>
                <span>
                  {isFetchError
                    ? 'Failed to load triage agent settings. Please try again later.'
                    : 'Failed to update triage agent settings. Please try again.'}
                </span>
              </Banner>
            )}

            {!isFetchError && (
              <>
                <div className={styles.toggleRow}>
                  <FormControl>
                    <FormControl.Label id="triage-agent-toggle-label" className={styles.toggleLabel}>
                      Enable triage agent
                    </FormControl.Label>
                  </FormControl>
                  <ToggleSwitch
                    size="small"
                    aria-labelledby="triage-agent-toggle-label"
                    checked={data?.enabled ?? false}
                    onClick={handleToggle}
                    disabled={isUpdating}
                    loading={isUpdating}
                  />
                </div>

                <div className={styles.configLink}>
                  <Link href={configUrl} target="_blank">
                    Configure triage rules
                    <LinkExternalIcon size={12} className="ml-1" />
                  </Link>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Dialog>
  )
}

try{ TriageAgentDialog.displayName ||= 'TriageAgentDialog' } catch {}